import React, { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { apiClient } from '../api/client';
import { Layout } from '../components/Layout';

interface User {
  id: number;
  kakao_id: string;
  email: string | null;
  name: string | null;
  phone: string | null;
  profile_image: string | null;
  is_active: boolean;
  created_at: string;
  updated_at: string;
  last_login_at: string | null;
}

interface UsersResponse {
  users: User[];
  total: number;
  page: number;
  limit: number;
}

export const UsersPage: React.FC = () => {
  const [page, setPage] = useState(1);
  const [limit, setLimit] = useState(20);
  const [search, setSearch] = useState('');
  const [isActive, setIsActive] = useState<boolean | null>(null);
  const [searchInput, setSearchInput] = useState('');
  const queryClient = useQueryClient();

  const { data: usersData, isLoading, error } = useQuery<UsersResponse>({
    queryKey: ['users', page, limit, search, isActive],
    queryFn: async () => {
      const params = new URLSearchParams();
      params.append('skip', ((page - 1) * limit).toString());
      params.append('limit', limit.toString());
      if (search) params.append('search', search);
      if (isActive !== null) params.append('is_active', isActive.toString());

      const response = await apiClient.get<UsersResponse>(`/users?${params.toString()}`);
      return response.data;
    },
  });

  const updateUserMutation = useMutation({
    mutationFn: async ({ userId, is_active }: { userId: number; is_active: boolean }) => {
      const response = await apiClient.patch(`/users/${userId}`, { is_active });
      return response.data;
    },
    onSuccess: () => {
      alert('회원 상태가 변경되었습니다.');
      queryClient.invalidateQueries({ queryKey: ['users'] });
    },
    onError: (error: any) => {
      const errorMessage = error?.response?.data?.detail || '상태 변경에 실패했습니다.';
      alert(errorMessage);
    },
  });

  const handleSearch = () => {
    setSearch(searchInput);
    setPage(1);
  };

  const handleReset = () => {
    setSearchInput('');
    setSearch('');
    setIsActive(null);
    setPage(1);
  };

  const handleToggleActive = (userId: number, currentStatus: boolean) => {
    if (window.confirm(`회원을 ${currentStatus ? '비활성화' : '활성화'} 하시겠습니까?`)) {
      updateUserMutation.mutate({ userId, is_active: !currentStatus });
    }
  };

  const users = usersData?.users || [];
  const total = usersData?.total || 0;

  return (
    <Layout>
      <div className="px-2 py-4">
        <h1 className="text-2xl font-semibold text-gray-900 mb-4">회원 관리</h1>

        {/* Search Form */}
        <div className="bg-white border border-slate-200 rounded-lg px-3 py-3 mb-4">
          <div className="flex items-end gap-3 flex-wrap">
            <div className="flex-1 min-w-[200px]">
              <label className="block text-sm font-medium text-gray-700 mb-1">
                검색어
              </label>
              <input
                type="text"
                value={searchInput}
                onChange={(e) => setSearchInput(e.target.value)}
                onKeyPress={(e) => e.key === 'Enter' && handleSearch()}
                className="block w-full rounded-md border border-gray-300 focus:border-indigo-500 focus:ring-1 focus:ring-indigo-500 text-sm px-3 py-2"
                placeholder="이름, 이메일, 전화번호"
              />
            </div>

            <div className="flex-shrink-0">
              <label className="block text-sm font-medium text-gray-700 mb-1">
                활성 상태
              </label>
              <select
                value={isActive === null ? '' : isActive.toString()}
                onChange={(e) => {
                  setIsActive(e.target.value === '' ? null : e.target.value === 'true');
                  setPage(1);
                }}
                className="block w-32 rounded-md border border-gray-300 focus:border-indigo-500 focus:ring-1 focus:ring-indigo-500 text-sm px-3 py-2"
              >
                <option value="">전체</option>
                <option value="true">활성</option>
                <option value="false">비활성</option>
              </select>
            </div>

            <div className="flex-shrink-0">
              <label className="block text-sm font-medium text-gray-700 mb-1">
                페이지당 행 수
              </label>
              <select
                value={limit}
                onChange={(e) => {
                  setLimit(Number(e.target.value));
                  setPage(1);
                }}
                className="block w-32 rounded-md border border-gray-300 focus:border-indigo-500 focus:ring-1 focus:ring-indigo-500 text-sm px-3 py-2"
              >
                <option value={20}>20개</option>
                <option value={50}>50개</option>
                <option value={100}>100개</option>
              </select>
            </div>

            <button
              onClick={handleSearch}
              className="px-3 py-1.5 bg-indigo-600 text-white text-sm rounded-md hover:bg-indigo-700"
            >
              조회
            </button>
            <button
              onClick={handleReset}
              className="px-3 py-1.5 bg-gray-200 text-gray-700 text-sm rounded-md hover:bg-gray-300"
            >
              초기화
            </button>
          </div>
        </div>

        {/* Results */}
        <div>
          <div className="flex items-center justify-between mb-3">
            <h2 className="text-lg font-semibold text-gray-900">
              회원 목록
              {total > 0 && <span className="ml-2 text-sm font-normal text-gray-600">({total}건)</span>}
            </h2>
          </div>

          {isLoading ? (
            <div className="bg-white border border-slate-200 rounded-lg px-6 py-12 text-center">
              <div className="inline-block animate-spin rounded-full h-8 w-8 border-b-2 border-slate-600"></div>
              <p className="mt-3 text-slate-600 text-sm">로딩 중...</p>
            </div>
          ) : error ? (
            <div className="bg-white border border-slate-200 rounded-lg px-6 py-12 text-center">
              <p className="text-rose-600 text-sm">회원 조회에 실패했습니다.</p>
            </div>
          ) : users.length === 0 ? (
            <div className="bg-white border border-slate-200 rounded-lg px-6 py-12 text-center">
              <p className="text-slate-500 text-sm">회원이 없습니다.</p>
            </div>
          ) : (
            <div className="bg-white border border-slate-200 rounded-lg overflow-hidden">
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-4 py-3 text-left text-sm font-medium text-gray-500 uppercase">ID</th>
                    <th className="px-4 py-3 text-left text-sm font-medium text-gray-500 uppercase">이름</th>
                    <th className="px-4 py-3 text-left text-sm font-medium text-gray-500 uppercase">이메일</th>
                    <th className="px-4 py-3 text-left text-sm font-medium text-gray-500 uppercase">연락처</th>
                    <th className="px-4 py-3 text-left text-sm font-medium text-gray-500 uppercase">상태</th>
                    <th className="px-4 py-3 text-left text-sm font-medium text-gray-500 uppercase">가입일</th>
                    <th className="px-4 py-3 text-left text-sm font-medium text-gray-500 uppercase">최근 로그인</th>
                    <th className="px-4 py-3 text-left text-sm font-medium text-gray-500 uppercase">액션</th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {users.map((user: User) => (
                    <tr key={user.id} className="hover:bg-gray-50">
                      <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-900">
                        {user.id}
                      </td>
                      <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-900">
                        {user.name || '-'}
                      </td>
                      <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-900">
                        {user.email || '-'}
                      </td>
                      <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-900">
                        {user.phone || '-'}
                      </td>
                      <td className="px-4 py-3 whitespace-nowrap text-sm">
                        <span className={`px-2 py-0.5 rounded text-xs font-medium ${
                          user.is_active
                            ? 'bg-green-100 text-green-800'
                            : 'bg-red-100 text-red-800'
                        }`}>
                          {user.is_active ? '활성' : '비활성'}
                        </span>
                      </td>
                      <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-500">
                        {new Date(user.created_at).toLocaleDateString('ko-KR')}
                      </td>
                      <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-500">
                        {user.last_login_at
                          ? new Date(user.last_login_at).toLocaleDateString('ko-KR')
                          : '-'}
                      </td>
                      <td className="px-4 py-3 whitespace-nowrap text-sm">
                        <button
                          onClick={() => handleToggleActive(user.id, user.is_active)}
                          className="inline-flex items-center justify-center h-8 px-2.5 rounded-md border bg-white shadow-sm hover:bg-gray-50 transition-colors"
                          title={user.is_active ? '비활성화' : '활성화'}
                        >
                          {user.is_active ? (
                            <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="text-red-600">
                              <circle cx="12" cy="12" r="10"></circle>
                              <path d="m4.9 4.9 14.2 14.2"></path>
                            </svg>
                          ) : (
                            <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="text-green-600">
                              <circle cx="12" cy="12" r="10"></circle>
                              <path d="M12 8v8"></path>
                              <path d="M8 12h8"></path>
                            </svg>
                          )}
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>

              {/* Pagination */}
              <div className="bg-white px-4 py-3 flex items-center justify-between border-t border-gray-200">
                <div>
                  <p className="text-xs text-gray-600">
                    페이지 <span className="font-medium text-gray-900">{page}</span> / {Math.ceil(total / limit)}
                  </p>
                </div>
                <div className="flex gap-2">
                  <button
                    onClick={() => setPage(Math.max(1, page - 1))}
                    disabled={page === 1}
                    className="px-3 py-1.5 border border-gray-300 rounded-md text-xs font-medium text-gray-700 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    이전
                  </button>
                  <button
                    onClick={() => setPage(page + 1)}
                    disabled={users.length < limit}
                    className="px-3 py-1.5 border border-gray-300 rounded-md text-xs font-medium text-gray-700 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    다음
                  </button>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>
    </Layout>
  );
};
